import { useState, useRef, useEffect } from 'react';



interface AntiBotProtectionConfig {
  minFormTime?: number;
  maxFormTime?: number;
  minSubmitInterval?: number;
  minMouseMovements?: number;
  minKeyboardEvents?: number;
  minFocusEvents?: number;
  minInteractionVariance?: number;
  maxFastFieldsPercentage?: number;
  minFieldInteractionTime?: number;
}

interface AntiBotProtectionReturn {
  // States
  formStartTime: number;
  mouseMovements: number;
  keyboardEvents: number;
  focusEvents: number;
  isHuman: boolean;
  fieldInteractionTimes: Record<string, number>;
  
  // Handlers
  handleFieldFocus: (fieldName: string) => void;
  handleFieldBlur: (fieldName: string) => void;
  validateHumanBehavior: () => { isValid: boolean; reason?: string };
  resetProtection: () => void;
  
  // Refs
  formRef: React.RefObject<HTMLDivElement | null>;
  lastSubmitTime: React.MutableRefObject<number>;
}

const defaultConfig: Required<AntiBotProtectionConfig> = {
  minFormTime: 5000, // 5 seconds
  maxFormTime: 1800000, // 30 minutes
  minSubmitInterval: 3000, // 3 seconds
  minMouseMovements: 5,
  minKeyboardEvents: 3,
  minFocusEvents: 2,
  minInteractionVariance: 100, // 100ms
  maxFastFieldsPercentage: 0.7, // 70%
  minFieldInteractionTime: 500, // 500ms
};

export const useAntiBotProtection = (
  config: AntiBotProtectionConfig = {}
): AntiBotProtectionReturn => {
  const finalConfig = { ...defaultConfig, ...config };
  
  // States
  const [formStartTime, setFormStartTime] = useState<number>(0);
  const [mouseMovements, setMouseMovements] = useState<number>(0);
  const [keyboardEvents, setKeyboardEvents] = useState<number>(0);
  const [focusEvents, setFocusEvents] = useState<number>(0);
  const [isHuman, setIsHuman] = useState(false);
  const [fieldInteractionTimes, setFieldInteractionTimes] = useState<Record<string, number>>({});
  
  // Refs
  const lastSubmitTime = useRef<number>(0);
  const formRef = useRef<HTMLDivElement>(null);



  // Track field interaction times
  const handleFieldFocus = (fieldName: string) => {
    const currentTime = Date.now();
    setFieldInteractionTimes(prev => ({
      ...prev,
      [`${fieldName}_start`]: currentTime
    }));
  };

  const handleFieldBlur = (fieldName: string) => {
    const currentTime = Date.now();
    setFieldInteractionTimes(prev => {
      const startTime = prev[`${fieldName}_start`];
      if (startTime) {
        const interactionTime = currentTime - startTime;
        return {
          ...prev,
          [fieldName]: interactionTime
        };
      }
      return prev;
    });
  };

  // Validate human behavior
  const validateHumanBehavior = (): { isValid: boolean; reason?: string } => {
    const currentTime = Date.now();
    const timeSinceStart = currentTime - formStartTime;
    const timeSinceLastSubmit = currentTime - lastSubmitTime.current;

    // 1. Minimum form time
    if (timeSinceStart < finalConfig.minFormTime) {
      return { isValid: false, reason: "Form completed too quickly" };
    }

    // 2. Maximum form time
    if (timeSinceStart > finalConfig.maxFormTime) {
      return { isValid: false, reason: "Form session expired" };
    }

    // 3. Rate limiting
    if (timeSinceLastSubmit < finalConfig.minSubmitInterval && lastSubmitTime.current > 0) {
      return { isValid: false, reason: "Please wait before submitting again" };
    }

    // 4. Check human interactions
    if (mouseMovements < finalConfig.minMouseMovements) {
      return { isValid: false, reason: "Insufficient mouse interaction" };
    }

    if (keyboardEvents < finalConfig.minKeyboardEvents) {
      return { isValid: false, reason: "Insufficient keyboard interaction" };
    }

    if (focusEvents < finalConfig.minFocusEvents) {
      return { isValid: false, reason: "Insufficient focus events" };
    }

    // 5. Check interaction patterns
    const interactionTimes = Object.values(fieldInteractionTimes);
    if (interactionTimes.length >= 3) {
      const avgTime = interactionTimes.reduce((a, b) => a + b, 0) / interactionTimes.length;
      const variance = interactionTimes.reduce((acc, time) => acc + Math.pow(time - avgTime, 2), 0) / interactionTimes.length;
      
      if (variance < finalConfig.minInteractionVariance) {
        return { isValid: false, reason: "Suspicious interaction pattern detected" };
      }
      
      const tooFastFields = interactionTimes.filter(time => time < finalConfig.minFieldInteractionTime).length;
      if (tooFastFields > interactionTimes.length * finalConfig.maxFastFieldsPercentage) {
        return { isValid: false, reason: "Fields completed too quickly" };
      }
    }

    return { isValid: true };
  };

  // Reset protection state
  const resetProtection = () => {
    setMouseMovements(0);
    setKeyboardEvents(0);
    setFocusEvents(0);
    setFormStartTime(Date.now());
    setIsHuman(false);
    setFieldInteractionTimes({});
  };

  // Initialize form start time and event listeners
  useEffect(() => {
    setFormStartTime(Date.now());
    
    const handleMouseMove = () => {
      setMouseMovements(prev => prev + 1);
    };
    
    const handleKeyDown = () => {
      setKeyboardEvents(prev => prev + 1);
    };
    
    const handleFocus = () => {
      setFocusEvents(prev => prev + 1);
    };

    const formElement = formRef.current;
    if (formElement) {
      formElement.addEventListener('mousemove', handleMouseMove);
      formElement.addEventListener('keydown', handleKeyDown);
      formElement.addEventListener('focusin', handleFocus);
    }

    return () => {
      if (formElement) {
        formElement.removeEventListener('mousemove', handleMouseMove);
        formElement.removeEventListener('keydown', handleKeyDown);
        formElement.removeEventListener('focusin', handleFocus);
      }
    };
  }, []);

  // Check if user behavior seems human
  useEffect(() => {
    if (mouseMovements >= finalConfig.minMouseMovements && 
        keyboardEvents >= finalConfig.minKeyboardEvents && 
        focusEvents >= finalConfig.minFocusEvents) {
      setIsHuman(true);
    }
  }, [mouseMovements, keyboardEvents, focusEvents, finalConfig]);

  return {
    // States
    formStartTime,
    mouseMovements,
    keyboardEvents,
    focusEvents,
    isHuman,
    fieldInteractionTimes,
    
    // Handlers
    handleFieldFocus,
    handleFieldBlur,
    validateHumanBehavior,
    resetProtection,
    
    // Refs
    formRef,
    lastSubmitTime,
  };
}; 